import { router } from '@/router/router'
import { getBaseURL } from './baseUrl'

/**
 * Removes BaseURL prefixes from relative URLs.
 * BaseURLs like '/meine-zurich/mz' or '/meine-da-direkt/da' are automatically removed.
 * Also handles full BaseURLs like '/de-de/meine-zurich/mz'.
 *
 * @param url - The URL to process
 * @returns The URL without BaseURL prefix
 */
function removeBaseURLFromRelativeUrl(url: string): string {
  if (!url.startsWith('/')) {
    return url
  }

  const baseURL = getBaseURL()

  // Remove full BaseURL if it exists at the start of the URL
  if (url.startsWith(baseURL)) {
    return url.substring(baseURL.length) || '/'
  }

  // Also check for common BaseURL patterns that might be in the URL
  // These are parts of the BaseURL that might appear in teaser URLs
  const baseUrlPatterns = [
    '/meine-zurich/mz',
    '/meine-da-direkt/da',
    '/de-de/meine-zurich/mz'
  ]

  for (const pattern of baseUrlPatterns) {
    if (url.startsWith(pattern)) {
      return url.substring(pattern.length) || '/'
    }
  }

  return url
}

/**
 * Checks if a URL is a relative URL (starts with /)
 *
 * @param url - The URL to check
 * @returns True if the URL is relative
 */
export function isRelativeUrl(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//')
}

export function navigate(
  url: string,
  forceReload: boolean = false,
  sameTab: boolean = false
): void {
  const urlWithBase = (
    globalThis.ENV_CONFIG?.BASE_URL + url.replace(globalThis.ENV_CONFIG?.BASE_URL, '')
  ).replaceAll(/([^:])\/\/+/g, '$1/')
  const urlWithoutBase = url.replace(globalThis.ENV_CONFIG?.BASE_URL, '')

  if (url.startsWith('tel:') || url.startsWith('mailto:')) {
    window.open(url, '_self')
  } else if (url.startsWith('http') && !sameTab) {
    window.open(url, '_blank')
  } else if (url.startsWith('http') && sameTab) {
    window.open(url, '_self')
  } else if (forceReload) {
    globalThis.location.assign(urlWithBase)
  } else if (urlExistsInRouter(urlWithoutBase)) {
    const { path, query } = router.resolve(urlWithoutBase)
    router
      .push({ path, query })
      .then(() => {
        window.scrollTo(0, 0)
      })
      .catch((e) => {
        console.error(e)
      }) // Fehler beim Navigieren abfangen
  } else {
    globalThis.location.assign(urlWithBase)
    window.scrollTo(0, 0)
  }
}

/**
 * Handles navigation for teaser buttons, supporting both relative and absolute URLs.
 * Relative URLs (starting with /) are handled as internal navigation using the router.
 * Absolute URLs (starting with http/https) are opened using window.open.
 *
 * @param url - The URL to navigate to
 * @param target - The target attribute (_self, _blank, etc.)
 */
export function handleTeaserButtonNavigation(url: string, target: string): void {
  if (!url || url === '') {
    throw new Error('Url path is not available!')
  }

  if (isRelativeUrl(url)) {
    const urlWithoutBase = removeBaseURLFromRelativeUrl(url)

    if (target === '_blank') {
      const baseURL = getBaseURL()
      const fullUrl = globalThis.location.origin + baseURL + urlWithoutBase
      window.open(fullUrl, '_blank')
    } else if (urlExistsInRouter(urlWithoutBase)) {
      const { path, query } = router.resolve(urlWithoutBase)
      router.push({ path, query }).then(() => {
        window.scrollTo(0, 0)
      })
    } else {
      navigate(urlWithoutBase, false, false)
    }
  } else if (
    target === '_self' ||
    target === '_parent' ||
    target === '_blank' ||
    target === '_top'
  ) {
    window.open(url, target)
  } else {
    window.open(url, '_blank')
  }
}

export function urlExistsInRouter(url: string) {
  try {
    const matchedRoute = router.resolve(url)
    // Check if route exists - matched.length > 0 means route was found
    // Also check if the resolved path is not empty (router.resolve returns empty path for unmatched routes in some cases)
    return matchedRoute.matched.length > 0 && matchedRoute.path !== ''
  } catch {
    // If resolve throws an error, route doesn't exist
    return false
  }
}
